import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone } from "lucide-react";
import { Reveal, RevealLines } from "./Reveal";
import { BRAND } from "../../lib/brand";

const services = [
  {
    id: "maderotherapie",
    tag: "Soin sculptant",
    title: "Maderothérapie",
    italic: "l'art du bois",
    audience:
      "Pour celles qui veulent redessiner leur silhouette, relancer la circulation, et affiner le grain de peau.",
    body: "Technique colombienne de massage modelant utilisant des outils en bois spécifiques. La pression ferme et rythmée sculpte les tissus, réveille la microcirculation, et laisse une peau visiblement plus tonique dès la première séance.",
    highlights: [
      "Sculpte la silhouette",
      "Active la circulation sanguine et lymphatique",
      "Lisse le grain de peau",
    ],
    image: "/images/madero.png",
    number: "01",
  },
  {
    id: "drainage",
    tag: "Drainage lymphatique",
    title: "Drainage lymphatique",
    italic: "l'apaisement liquide",
    audience:
      "Pour celles qui veulent des jambes légères, et diminuer la rétention d'eau.",
    body: "Une gestuelle lente, douce, ininterrompue. Les mains suivent la carte lymphatique du corps, relancent le flux, évacuent les stagnations. Ce soin procure légèreté et bien-être.",
    highlights: [
      "Active la circulation lymphatique",
      "Élimine les toxines",
      "Réduit la rétention d'eau",
      "Sensation de légèreté immédiate",
    ],
    image: "/images/drainage.png",
    number: "02",
  },
];

function ServiceBlock({ s, reverse }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <div
      ref={ref}
      className={`grid md:grid-cols-12 gap-10 md:gap-16 items-center py-16 md:py-28 ${
        reverse ? "" : ""
      }`}
      data-testid={`service-${s.id}`}
    >
      <div
        className={`md:col-span-6 relative ${reverse ? "md:order-2" : ""}`}
      >
        <motion.div style={{ y }} className="relative aspect-[4/5] w-full">
          <div
            className={`absolute inset-0 overflow-hidden ${
              reverse ? "frame-clip-alt" : "frame-clip"
            }`}
          >
            <img
              src={s.image}
              alt={s.title}
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div className="grain absolute inset-0" />
          </div>
          <div className="absolute -top-8 -left-3 md:-left-8 chapter-number text-5xl md:text-6xl">
            {s.number}.
          </div>
        </motion.div>
      </div>

      <div className={`md:col-span-6 ${reverse ? "md:order-1" : ""}`}>
        <Reveal>
          <span className="text-[11px] uppercase tracking-[0.3em] text-gold">
            {s.tag}
          </span>
        </Reveal>

        <RevealLines
          lines={[s.title]}
          delayBase={0.1}
          className="mt-4 font-serif text-olive text-6xl md:text-7xl leading-[0.95] tracking-tighter"
        />
        <RevealLines
          lines={[s.italic]}
          delayBase={0.25}
          className="font-serif italic text-gold text-4xl md:text-5xl mt-2 leading-[1]"
        />

        <Reveal delay={0.35}>
          <p className="mt-8 text-olive/80 text-[17px] leading-[1.75] font-light max-w-[520px]">
            {s.body}
          </p>
        </Reveal>

        <Reveal delay={0.45} className="mt-8">
          <div className="flex flex-col gap-3">
            {s.highlights.map((h, i) => (
              <div
                key={i}
                className="flex items-baseline gap-4 border-b border-olive/15 pb-3"
              >
                <span className="chapter-number text-sm w-8">
                  0{i + 1}
                </span>
                <span className="text-olive/90 text-[15px]">{h}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.55} className="mt-8 flex flex-wrap items-center gap-6">
          <div className="text-[13px] text-olive/70 max-w-sm italic">
            {s.audience}
          </div>
        </Reveal>

        <Reveal delay={0.65} className="mt-10 flex flex-wrap items-center gap-6">
          <a
            href={BRAND.phoneHref}
            data-testid={`cta-reserve-${s.id}`}
            className="btn-gold inline-flex items-center gap-3 px-7 py-4 text-[12px] uppercase tracking-[0.24em] rounded-full"
          >
            <Phone className="h-4 w-4" strokeWidth={1.6} />
            Réserver ce soin
          </a>
        </Reveal>
      </div>
    </div>
  );
}

export default function Services() {
  return (
    <section
      id="soins"
      className="relative bg-cream"
      data-testid="services-section"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-10">
        <div className="pt-20 md:pt-32 pb-4 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-6">
            <Reveal>
              <div className="flex items-baseline gap-4">
                <span className="chapter-number text-3xl md:text-4xl">02</span>
                <span className="text-[11px] uppercase tracking-[0.3em] text-olive/60">
                  Les Soins
                </span>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <RevealLines
              lines={["Deux rituels, un soin signature,", "l'art du toucher."]}
              className="font-serif text-olive text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tighter"
            />
            <Reveal delay={0.3}>
              <p className="mt-6 text-olive/70 text-[15px] leading-[1.7] font-light max-w-[520px]">
                Chaque protocole est adapté à votre corps, à votre rythme, à ce
                que vous êtes venue chercher. Rien n'est standardisé. Tout est
                pensé pour vous.
              </p>
            </Reveal>
          </div>
        </div>

        {services.map((s, i) => (
          <ServiceBlock key={s.id} s={s} reverse={i % 2 === 1} />
        ))}
      </div>
    </section>
  );
}
